import { useContext, useEffect, useState } from 'react';
import { EventAccordionItemProps } from 'src/components/Accordions';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { useEventLastReviewedContext } from 'src/contexts/SellerEventLastReviewContext/SellerEventLastReviewContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { globalMobileWrapper } from 'src/core/themes/themeContract.css';
import { Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { InventoryDeeplinkQueryParam } from 'src/utils/constants/constants';
import { getDeepLinkIdFromUrl } from 'src/utils/deepLinkUtils';
import { getListingDetailsModalConfigWithDeepLink } from 'src/utils/inventoryUtils';
import {
  EventWithData,
  Feature,
  Listing,
  ListingDetailDataField,
  ListingDetails,
} from 'src/WebApiController';

import { useInventoryEventLocalContext } from '../InventoryEventLocalContext';
import { InventoryEventPageContent } from '../InventoryEventPageContent';
import { InventoryEventHeaderMobile } from './EventHeader/InventoryEventHeaderMobile';

export type InventoryEventContentMobileProps = {
  accordionItemProps?: EventWithData;
  inventoryAccordionItem?: EventAccordionItemProps<Listing>;
  returnUrl?: string;
};

export function InventoryEventContentMobile({
  accordionItemProps,
  inventoryAccordionItem,
  returnUrl,
}: InventoryEventContentMobileProps) {
  const { isLoading } = useCatalogDataContext();
  const hasMobileInventoryDetailsViewFeature = useUserHasFeature(
    Feature.MobileInventoryDetailsView
  );

  const { setModal } = useContext(ModalContext);
  const { posEntity: listing, setActivePosEntity } =
    useActivePosEntityContext<ListingDetails>();

  const activeId = getDeepLinkIdFromUrl(
    InventoryDeeplinkQueryParam,
    window.location.href
  );

  useEffect(() => {
    if (activeId?.length && !listing && !hasMobileInventoryDetailsViewFeature) {
      setActivePosEntity(parseInt(activeId), undefined, true, [
        ListingDetailDataField.Basic,
      ]);
      setModal(getListingDetailsModalConfigWithDeepLink(true, activeId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, hasMobileInventoryDetailsViewFeature]);

  const event = accordionItemProps?.event;

  const { onSettingVirtualIds } = useEventLastReviewedContext();

  useEffect(() => {
    onSettingVirtualIds(event?.virtId ? [event?.virtId] : []);
  }, [event?.virtId, onSettingVirtualIds]);

  if (isLoading || !event) {
    return <PosSpinner />;
  }

  return (
    <Stack
      direction="column"
      width="full"
      height="full"
      className={globalMobileWrapper}
    >
      <InventoryEventHeaderMobile
        event={accordionItemProps}
        inventoryAccordionItem={inventoryAccordionItem}
        returnUrl={returnUrl}
      />
      <InventoryEventPageContentWrapper
        accordionItemProps={accordionItemProps}
        inventoryAccordionItem={inventoryAccordionItem}
      />
    </Stack>
  );
}

const InventoryEventPageContentWrapper = ({
  accordionItemProps,
  inventoryAccordionItem,
}: Omit<InventoryEventContentMobileProps, 'returnUrl'>) => {
  const [selectedListingIds, setSelectedListingIds] = useState<number[]>([]);

  const { activeTab } = useInventoryEventLocalContext();
  return (
    <Stack
      width="full"
      height="full"
      style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
    >
      <InventoryEventPageContent
        event={accordionItemProps?.event}
        accordionItemProps={accordionItemProps}
        inventoryAccordionItem={inventoryAccordionItem}
        selectedListingIds={selectedListingIds}
        setSelectedListingIds={setSelectedListingIds}
        activeTab={activeTab}
      />
    </Stack>
  );
};
